import React, { useEffect } from "react";
import EnrolledFactor, { EnrolledFactorProps } from "./EnrolledFactor";
import { List, ListItem, Loader } from "@athena/forge";
import Header from "../common/Header";
import AvailableFactor, { AvailableFactorProps } from "./AvailableFactor";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";
import {
  getAvailableFactorsAndAuthenticatorInfo,
  getEnrolledFactors,
  removeEnrolledFactor,
  sessionExists,
} from "../../api/Api";
import { useOktaAuth } from "@okta/okta-react";
import "./ManageFactors.css";
import {
  factorIconMap,
  factorDescriptionMap,
  factorDisplayNameMap,
  MAX_PHONE_FACTOR_ALLOWED,
  MAX_WEBAUTHN_FACTOR_ALLOWED,
  MFA_FACTORS_BLACK_LIST,
  normalizeFactorType,
} from "../../lib/constants";
import config from "../../lib/config";
import PageLabel from "../common/PageLabel";
import { MFAEnrolledFactorAPI } from "../../types/ApiResponse.type";
import { logLandingEvent } from "../../lib/amplitude-utils";
// Note: We intentionally avoid using oktaAuth.session.exists() directly here.
// Instead, we use our API helper `sessionExists()` which calls Okta's Sessions API
// (`/api/v1/sessions/me`) using the configured `oktaURL`. This mirrors the
// behavior of oktaAuth.session.exists() but works reliably in flows where the
// Sign-In Widget may redirect before mounting.

const MANAGEFACTORS_ROUTE = "/managefactors";

const ManageFactors = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { oktaAuth, authState } = useOktaAuth();
  const oktaUrl = config.oktaURL;
  const clientId = config.oauthClientId;
  const baseUrl = `${oktaUrl}idp/authenticators/setup/`;
  const factorSetupUrlMap: { [key: string]: string } = {};

  // `factorType` must already be normalized (see normalizeFactorType) to one of
  // factorSetupUrlMap's canonical keys before this handler is called.
  const setUpFactorHandler = async (factorType: string): Promise<void> => {
    const setupUrl = factorSetupUrlMap[factorType];
    const isSessionExisting = await sessionExists();

    if (!isSessionExisting) {
      oktaAuth.setOriginalUri(MANAGEFACTORS_ROUTE);
      await oktaAuth.signInWithRedirect();
      return;
    }

    window.location.href = setupUrl;
  };

  const maskPhoneNumber = (phoneNumber: string | undefined): string => {
    if (!phoneNumber) return "";
    return phoneNumber;
  };

  const footerLinks = [
    {
      text: t("footer.button.backtoprofile"),
      href: "/",
    },
  ];

  const [enrolledFactors, setEnrolledFactors] = React.useState<
    EnrolledFactorProps[]
  >([]);
  const [availableFactors, setAvailableFactors] = React.useState<
    AvailableFactorProps[]
  >([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  useEffect(() => {
    const removeFactorHandler = async (factorId: string): Promise<void> => {
      setIsLoading(true);
      // Similar to setup, verify there is an Okta session before attempting to remove factors
      // through our backend proxy. If no session exists, redirect back to Manage Factors.
      const isSessionExisting = await sessionExists();
      if (isSessionExisting) {
        await removeEnrolledFactor(factorId, authState);
        await fetchData();
        setIsLoading(false);
      }
      else {
        window.location.href = MANAGEFACTORS_ROUTE;
      }
    };
    const fetchData = async (): Promise<void> => {
      const enrolledFactorsAPI: MFAEnrolledFactorAPI[] =
        await getEnrolledFactors(authState);
      const { factorInfo, authenticatorInfo } =
        await getAvailableFactorsAndAuthenticatorInfo(authState);
      // Filter out available factors that are already enrolled. Both sides are
      // normalized because Okta names the same factor differently per endpoint —
      // see normalizeFactorType.
      let availableFactorsForSetup = factorInfo.filter(
        (factor) =>
          !enrolledFactorsAPI.some(
            (enrolledFactor) =>
              normalizeFactorType(enrolledFactor.factorType) ===
              normalizeFactorType(factor.factorType),
          ),
      );

      factorSetupUrlMap["question"] = `${baseUrl}${authenticatorInfo["security_question"]}?factorType=SECURITY_QUESTION&manageFactor=true&clientId=${clientId}`;
      factorSetupUrlMap["phone"] = `${baseUrl}${authenticatorInfo["phone_number"]}?factorType=PHONE_FACTOR&manageFactor=true&clientId=${clientId}`;
      factorSetupUrlMap["webauthn"] = `${baseUrl}${authenticatorInfo["webauthn"]}?factorType=WEBAUTHN_FACTOR&manageFactor=true&clientId=${clientId}`;
      factorSetupUrlMap["google_otp"] = `${baseUrl}${authenticatorInfo["google_otp"]}?factorType=GOOGLE_OTP_FACTOR&manageFactor=true&clientId=${clientId}`;

      const countEnrolledPhones = enrolledFactorsAPI.reduce(
        (count, factor) => {
          if (factor.factorType === "phone") {
            count++;
          }
          return count;
        },
        0,
      );

      // Okta represents Phone as sms/call, so the app always creates a single
      // synthetic Phone option. WebAuthn is returned directly and is policy-gated.
      // Preserve whether Okta offered it before applying our local enrollment cap.
      const webauthnOfferedByOkta = factorInfo.some(
        (factor) => normalizeFactorType(factor.factorType) === "webauthn",
      );
      const countEnrolledWebauthn = enrolledFactorsAPI.reduce(
        (count, factor) =>
          normalizeFactorType(factor.factorType) === "webauthn"
            ? count + 1
            : count,
        0,
      );
      availableFactorsForSetup = availableFactorsForSetup.filter(
        (factor) =>
          factor.factorType !== "sms" &&
          factor.factorType !== "call" &&
          normalizeFactorType(factor.factorType) !== "webauthn",
      );
      if (countEnrolledPhones < MAX_PHONE_FACTOR_ALLOWED) {
        availableFactorsForSetup.push({
          factorType: "phone",
          provider: "OKTA",
          vendorName: "OKTA",
        });
      }
      if (
        webauthnOfferedByOkta &&
        countEnrolledWebauthn < MAX_WEBAUTHN_FACTOR_ALLOWED
      ) {
        availableFactorsForSetup.push({
          factorType: "webauthn",
          provider: "FIDO",
          vendorName: "FIDO",
        });
      }

      // Format the factor data get from API to fit the component parameter interface
      const enrolledFactors = enrolledFactorsAPI
        .filter((f) => !MFA_FACTORS_BLACK_LIST.includes(normalizeFactorType(f.factorType)))
        .map((factor) => {
          const canonicalType = normalizeFactorType(factor.factorType);
          return {
            id: factor.id,
            factorName: t(factorDisplayNameMap[canonicalType]),
            enrolledNumber: (factor.profile && factor.profile.phoneNumber)
              ? maskPhoneNumber(factor.profile.phoneNumber)
              : "",
            deviceLabel:
              canonicalType === "webauthn" ? factor.name : undefined,
            icon: factorIconMap[canonicalType],
            removeFactorHandler,
          };
        })
        .sort((a, b) => a.factorName.localeCompare(b.factorName));

      const availableFactors = availableFactorsForSetup
        .filter((f) => !MFA_FACTORS_BLACK_LIST.includes(normalizeFactorType(f.factorType)))
        .map((factor) => {
          const canonicalType = normalizeFactorType(factor.factorType);
          let buttonText = t("managefactors.availablefactor.buttontext");
          if (
            (canonicalType === "phone" &&
              enrolledFactors.some((f) => f.factorName === "Phone")) ||
            (canonicalType === "webauthn" && countEnrolledWebauthn > 0)
          ) {
            buttonText = t(
              "managefactors.availablefactor.oie.phone.buttontext",
            );
          }
          return {
            id: canonicalType,
            factorName: t(factorDisplayNameMap[canonicalType]),
            description: t(factorDescriptionMap[canonicalType]),
            icon: factorIconMap[canonicalType],
            setUpFactorHandler: () => setUpFactorHandler(canonicalType),
            buttonText: buttonText,
          };
        })
        .sort((a, b) => a.factorName.localeCompare(b.factorName));
      setEnrolledFactors(enrolledFactors);
      setAvailableFactors(availableFactors);
      setIsLoading(false);
    };
    fetchData();
    return () => {
      // cleanup
    };
  }, []);

  logLandingEvent({ pageName: "EditFactors" });

  return (
    <>
      <Header
        headerText={t("managefactors.page.title")}
        description={[t("managefactors.page.description")]}
      />
      {isLoading && (
        <Loader loading={isLoading} />
      )}
      {enrolledFactors.length > 0 && (
        <PageLabel text={t("managefactors.enrolledfactor.title")} />
      )}
      <List id="uiam-enrolled-factors">
        {enrolledFactors.map(
          ({
            id,
            factorName,
            enrolledNumber,
            deviceLabel,
            icon,
            removeFactorHandler,
          }) => (
            <ListItem uniqueKey={id} key={id}>
              <EnrolledFactor
                id={id}
                factorName={factorName}
                enrolledNumber={enrolledNumber}
                deviceLabel={deviceLabel}
                icon={icon}
                removeFactorHandler={removeFactorHandler}
              />
            </ListItem>
          ),
        )}
      </List>
      {enrolledFactors.length > 0 && availableFactors.length > 0 && (
        <PageLabel text={t("managefactors.availablefactor.title")} />
      )}
      <List id="uiam-available-factors">
        {availableFactors.map(
          ({ id, factorName, description, icon, buttonText, setUpFactorHandler }) => (
            <ListItem uniqueKey={id} key={id}>
              <AvailableFactor
                id={id}
                factorName={factorName}
                description={description}
                icon={icon}
                setUpFactorHandler={setUpFactorHandler}
                buttonText={buttonText}
              />
            </ListItem>
          ),
        )}
      </List>
      <Footer footerLinks={footerLinks} />
    </>
  );
};

export default ManageFactors;
